import { FileText } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import MultiSelect from "@/components/multi-select";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  useMailboxTemplateSettings,
  useUpdateMailboxTemplateSettings,
} from "@/hooks/use-mailbox";
import { MailboxSettings, MailboxTemplateType } from "@/types/mailbox.types";
import { useAllCompanyTemplates } from "@/hooks/use-template";
import { TemplateType } from "@/types/template.types";
import { toast } from "sonner";
import { Badge } from "../ui/badge";

type Props = {
  mailboxId: string;
  mailboxEmail: string;
};

type TemplateOption = {
  label: string;
  value: string;
};

const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

// Helper function to get day name
const getDayName = (dayIndex: number): string => {
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  return days[dayIndex];
};

const ScheduleTemplates = ({ mailboxId, mailboxEmail }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: mailboxTemplateSettings } = useMailboxTemplateSettings(
    mailboxId,
    true
  );

  const {
    mutate: updateMailboxTemplateSettings,
    isPending: isUpdatingSettings,
  } = useUpdateMailboxTemplateSettings();

  // Use all company templates for better performance
  const { data: templates, isLoading: isLoadingTemplates } = useAllCompanyTemplates();

  const [isAllDays, setIsAllDays] = useState(true);
  const [useAssignedTemplates, setUseAssignedTemplates] = useState(false);
  const [allDaysTemplate, setAllDaysTemplate] = useState<string[]>([]);
  const [individualTemplates, setIndividualTemplates] = useState<
    Record<string, string[]>
  >({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  });

  const handleAllDaysChange = (value: string) => {
    setAllDaysTemplate((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const handleIndividualChange = (day: string, value: string) => {
    setIndividualTemplates((prev) => ({
      ...prev,
      [day]: prev[day].includes(value)
        ? prev[day].filter((item) => item !== value)
        : [...prev[day], value],
    }));
  };

  const updateTemplateSettings = useCallback(
    (mailboxTemplateSettings: MailboxSettings | undefined) => {
      if (mailboxTemplateSettings) {
        const allDayType =
          mailboxTemplateSettings.templateType === MailboxTemplateType.ALL_DAYS;

        setIsAllDays(allDayType);
        setUseAssignedTemplates(mailboxTemplateSettings.useAssignedTemplates);
        setAllDaysTemplate(
          mailboxTemplateSettings.templateConfigurations.allDays?.templateIds ?? []
        );

        const transformed = Object.entries(
          mailboxTemplateSettings.templateConfigurations
        ).reduce((acc, [key, value]) => {
          if (value && typeof value === 'object' && 'templateIds' in value) {
            acc[key] = value.templateIds;
          }
          return acc;
        }, {} as Record<string, string[]>);

        setIndividualTemplates(transformed);
      }
    },
    []
  );

  useEffect(() => {
    updateTemplateSettings(mailboxTemplateSettings);
  }, [mailboxTemplateSettings, updateTemplateSettings]);

  const templateOptions = useMemo(() => {
    return (
      templates?.map((template: TemplateType) => ({
        label: template.title,
        value: template._id,
      })) ?? []
    );
  }, [templates]);

  const today = useMemo(() => new Date().getDay(), []);

  const selectedValues = useMemo(
    () => {
      if (useAssignedTemplates) {
        return new Set<string>();
      }
      return isAllDays
        ? new Set(allDaysTemplate)
        : new Set(individualTemplates[getDayName(today)]);
    },
    [useAssignedTemplates, isAllDays, allDaysTemplate, individualTemplates, today]
  );

  const selectedOptions = useMemo(
    () => templateOptions.filter((option: TemplateOption) => selectedValues.has(option.value)),
    [templateOptions, selectedValues]
  );

  const handleSave = () => {
    const payload = {
      monday: { templateIds: individualTemplates.monday },
      tuesday: { templateIds: individualTemplates.tuesday },
      wednesday: { templateIds: individualTemplates.wednesday },
      thursday: { templateIds: individualTemplates.thursday },
      friday: { templateIds: individualTemplates.friday },
      saturday: { templateIds: individualTemplates.saturday },
      sunday: { templateIds: individualTemplates.sunday },
      allDays: { templateIds: allDaysTemplate },
    };

    updateMailboxTemplateSettings(
      {
        mailboxId,
        settings: {
          templateType: isAllDays
            ? MailboxTemplateType.ALL_DAYS
            : MailboxTemplateType.WEEKDAYS,
          templateConfigurations: payload,
          useAssignedTemplates,
        },
      },
      {
        onSuccess: () => {
          toast.success("Templates scheduled successfully");
          setIsOpen(false);
          window.cioanalytics?.track("mailbox_templates_updated", {
            email: mailboxEmail,
          });
        },
        onError: () => {
          toast.error("Failed to schedule templates");
        },
      }
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(e) => {
        setIsOpen(e);
        if (!e) {
          updateTemplateSettings(mailboxTemplateSettings);
          setSearchTerm(""); // Reset search when dialog closes
        }
      }}
    >
      <TemplateDialogTrigger
        selectedValues={selectedValues}
        selectedOptions={selectedOptions}
        useAssignedTemplates={useAssignedTemplates}
      />
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Template Schedule</DialogTitle>
          <DialogDescription>
            Set templates for your weekly schedule. Search and select from all available templates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 my-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="use-assigned-templates"
              checked={useAssignedTemplates}
              onCheckedChange={setUseAssignedTemplates}
            />
            <Label htmlFor="use-assigned-templates">
              Use Assigned Templates
            </Label>
          </div>
          
          {useAssignedTemplates && (
            <div className="flex items-center space-x-2">
              <Switch
                id="schedule-mode"
                checked={isAllDays}
                onCheckedChange={setIsAllDays}
              />
              <Label htmlFor="schedule-mode">
                {isAllDays ? "All Days" : "Individual Days"}
              </Label>
            </div>
          )}
        </div>

        {useAssignedTemplates && (
          <>
            {isAllDays ? (
              <Card className="p-4 bg-muted/50">
                <div className="flex items-center gap-4 mb-2">
                  <Label htmlFor="all-days" className="min-w-[80px]">
                    All Days
                  </Label>
                  <MultiSelect
                    values={allDaysTemplate}
                    options={templateOptions}
                    onChange={handleAllDaysChange}
                    title="Templates"
                    placeholder="Search templates..."
                    className="flex-1"
                    onSearchChange={setSearchTerm}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  This template will be applied to all days of the week.
                </p>
              </Card>
            ) : (
              <Card className="p-4 bg-muted/50">
                <div className="space-y-4">
                  {daysOfWeek.map((day) => (
                    <div key={day} className="flex items-center gap-4">
                      <Label htmlFor={day} className="min-w-[80px] capitalize">
                        {day}
                      </Label>
                      <MultiSelect
                        values={individualTemplates[day]}
                        options={templateOptions}
                        onChange={(value) => handleIndividualChange(day, value)}
                        title="Select templates"
                        placeholder="Search templates..."
                        className="flex-1"
                        onSearchChange={setSearchTerm}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {!useAssignedTemplates && (
          <Card className="p-4 bg-muted/50">
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Using Default templates. No manual template assignment needed.
              </p>
            </div>
          </Card>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            type="submit"
            onClick={handleSave}
            disabled={isUpdatingSettings}
            isLoading={isUpdatingSettings}
          >
            {isUpdatingSettings ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplateDialogTrigger = ({
  selectedValues,
  selectedOptions,
  useAssignedTemplates,
}: {
  selectedValues: Set<string>;
  selectedOptions: TemplateOption[];
  useAssignedTemplates: boolean;
}) => {
  const EmptyState = () => (
    <span className="flex items-center gap-2">
      <FileText className="h-4 w-4" />
      <span>No templates selected</span>
    </span>
  );

  const AssignedTemplatesState = () => (
    <span className="flex items-center gap-2">
      <FileText className="h-4 w-4" />
      <span>Using assigned templates</span>
    </span>
  );

  const SelectedTemplates = () => (
    <>
      <div className="hidden space-x-1 lg:flex max-w-full">
        {selectedOptions.slice(0, 2).map((option) => (
          <Badge
            key={option.value}
            variant="secondary"
            className="rounded-sm px-1 font-normal truncate"
          >
            <span className="max-w-[70px] truncate">{option.label}</span>
          </Badge>
        ))}
        {selectedValues.size > 2 && (
          <RemainingEntriesTooltip
            remainingOptions={selectedOptions.slice(2)}
            count={selectedValues.size - 2}
          />
        )}
      </div>
      <Badge
        variant="secondary"
        className="rounded-sm px-1 font-normal lg:hidden"
      >
        {selectedValues.size}{" "}
        {selectedValues.size === 1 ? "template" : "templates"} selected
      </Badge>
    </>
  );

  return (
    <DialogTrigger asChild>
      <Button variant="outline" className="gap-2 w-full justify-start">
        {useAssignedTemplates ? (
          <AssignedTemplatesState />
        ) : selectedValues.size > 0 ? (
          <SelectedTemplates />
        ) : (
          <EmptyState />
        )}
      </Button>
    </DialogTrigger>
  );
};

// TODO: Move to a comoon component
const RemainingEntriesTooltip = ({
  remainingOptions,
  count,
}: {
  remainingOptions: TemplateOption[];
  count: number;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Badge variant="secondary" className="rounded-sm px-1 font-normal">
        +{count} more
      </Badge>
    </TooltipTrigger>
    <TooltipContent className="bg-black text-white">
      <TooltipArrow />
      <p>
        {remainingOptions.map((option) => (
          <span key={option.value}>
            {option.label}
            <br />
          </span>
        ))}
      </p>
    </TooltipContent>
  </Tooltip>
);

export default ScheduleTemplates;
